/**
 * Main ChatApp class - orchestrates all components.
 */

import {
    Language,
    ApiService,
    AuthService,
    LocationService,
    getElement,
    getClientTimeContext,
    ModelsResponse,
    UsersListResponse,
    normalizeCountryCode,
    haversineDistance,
} from './services';
import { ImageUpload, AuthModal, PhotoLightbox } from './ui_components';
import { i18n, setLanguage, getLanguage, applyTranslations } from './i18n';
import {
    Restaurant,
    TravelMode,
    TravelTimeResult,
    RestaurantsListResponse,
    CuisinesResponse,
    TagsResponse,
    RestaurantSortField,
    SortOrder,
    STORAGE_KEYS,
    LOCATION_REFRESH_INTERVAL_MS,
    MIN_GEOCODE_DISTANCE_METERS,
    RestaurantPhotosResponse,
} from './types';
import { AudioRecorder } from './audio_recorder';
import { SessionHistoryPanel, VisitHistoryPanel, FilterConfigPanel, SessionLoadedEvent } from './panels';
import { RestaurantFormManager } from './restaurant_form';
import { ChatView, FilterSettings } from './chat_view';

type ViewType = 'chat' | 'restaurants';

export class ChatApp {
    // Navigation elements
    private navTabs: NodeListOf<HTMLButtonElement>;
    private chatView: HTMLElement;
    private restaurantsView: HTMLElement;

    // Chat elements
    private chatContainer: HTMLElement;
    private messageInput: HTMLTextAreaElement;
    private chatForm: HTMLFormElement;
    private sendBtn: HTMLButtonElement;
    private cancelBtn: HTMLButtonElement;
    private clearBtn: HTMLButtonElement;
    private modelSelect: HTMLSelectElement;
    private langButtons: NodeListOf<HTMLButtonElement>;

    // Restaurant elements
    private restaurantList: HTMLElement;
    private addRestaurantBtn: HTMLButtonElement;
    private magicImportBtn: HTMLButtonElement;
    private restaurantSearchInput: HTMLInputElement;
    private restaurantSearchTimer: number | null = null;

    // Restaurant sorting elements
    private restaurantSortBy: HTMLSelectElement;
    private restaurantSortOrderBtn: HTMLButtonElement;
    private currentSortField: RestaurantSortField = 'last_visit';
    private currentSortOrder: SortOrder = 'desc';

    // User elements
    private userSelect: HTMLSelectElement;
    private addUserBtn: HTMLButtonElement;

    // Filter summary bar elements
    private filterSummaryBar: HTMLElement;
    private filterSummaryIcon: HTMLElement;
    private filterSummaryText: HTMLElement;
    private recommendActionBtn: HTMLButtonElement;

    // Mobile menu elements
    private mobileMenuBtn: HTMLButtonElement;
    private mobileMenu: HTMLElement;

    // Services
    private api: ApiService;
    private location: LocationService;
    private authService: AuthService;

    // Components
    private imageUpload: ImageUpload;
    private authModal: AuthModal;
    private audioRecorder: AudioRecorder;
    private photoLightbox: PhotoLightbox;
    private chatViewComponent: ChatView;
    private sessionHistoryPanel: SessionHistoryPanel;
    private visitHistoryPanel: VisitHistoryPanel;
    private filterConfigPanel: FilterConfigPanel;
    private restaurantForm: RestaurantFormManager;

    // State
    private sessionId: string;
    private selectedModel: string = '';
    private selectedUserId: string = 'DEBUG';
    private authToken: string = '';
    private isViewingHistory: boolean = false;
    private isSending: boolean = false;
    private travelTimes: Map<string, TravelTimeResult> = new Map();
    private restaurants: Restaurant[] = [];
    private locationRefreshIntervalId: number | null = null;

    constructor() {
        // Navigation elements
        this.navTabs = document.querySelectorAll<HTMLButtonElement>('.nav-tab');
        this.chatView = getElement<HTMLElement>('chatView');
        this.restaurantsView = getElement<HTMLElement>('restaurantsView');

        // Chat elements
        this.chatContainer = getElement<HTMLElement>('chatContainer');
        this.messageInput = getElement<HTMLTextAreaElement>('messageInput');
        this.chatForm = getElement<HTMLFormElement>('chatForm');
        this.sendBtn = getElement<HTMLButtonElement>('sendBtn');
        this.cancelBtn = getElement<HTMLButtonElement>('cancelBtn');
        this.clearBtn = getElement<HTMLButtonElement>('clearBtn');
        this.modelSelect = getElement<HTMLSelectElement>('modelSelect');
        this.langButtons = document.querySelectorAll<HTMLButtonElement>('.lang-btn');

        // Restaurant elements
        this.restaurantList = getElement<HTMLElement>('restaurantList');
        this.addRestaurantBtn = getElement<HTMLButtonElement>('addRestaurantBtn');
        this.magicImportBtn = getElement<HTMLButtonElement>('magicImportBtn');
        this.restaurantSearchInput = getElement<HTMLInputElement>('restaurantSearchInput');
        this.restaurantSortBy = getElement<HTMLSelectElement>('restaurantSortBy');
        this.restaurantSortOrderBtn = getElement<HTMLButtonElement>('restaurantSortOrderBtn');

        // User elements
        this.userSelect = getElement<HTMLSelectElement>('userSelect');
        this.addUserBtn = getElement<HTMLButtonElement>('addUserBtn');

        // Filter summary bar elements
        this.filterSummaryBar = getElement<HTMLElement>('filterSummaryBar');
        this.filterSummaryIcon = getElement<HTMLElement>('filterSummaryIcon');
        this.filterSummaryText = getElement<HTMLElement>('filterSummaryText');
        this.recommendActionBtn = getElement<HTMLButtonElement>('recommendActionBtn');

        // Mobile menu elements
        this.mobileMenuBtn = getElement<HTMLButtonElement>('mobileMenuBtn');
        this.mobileMenu = getElement<HTMLElement>('mobileMenu');

        this.sessionId = this.generateSessionId();

        // Initialize services
        this.api = new ApiService(() => this.getUserHeaders());
        this.location = new LocationService();
        this.authService = new AuthService(STORAGE_KEYS.AUTH_TOKEN);

        // Initialize auth modal
        this.authModal = new AuthModal(
            this.authService,
            () => i18n,
            () => this.loadUsers()
        );

        // Initialize image upload and photo lightbox
        this.imageUpload = new ImageUpload();
        this.photoLightbox = new PhotoLightbox();

        // Initialize audio recorder
        this.audioRecorder = new AudioRecorder(
            getElement<HTMLButtonElement>('audioRecordBtn'),
            getElement<HTMLElement>('audioPreviewContainer'),
            getElement<HTMLElement>('audioDuration'),
            getElement<HTMLButtonElement>('audioRemoveBtn')
        );

        // Initialize chat view component
        this.chatViewComponent = new ChatView(
            this.chatContainer,
            this.api,
            this.photoLightbox
        );

        // Initialize session history panel
        this.sessionHistoryPanel = new SessionHistoryPanel(
            getElement<HTMLElement>('historyPanel'),
            getElement<HTMLElement>('historyOverlay'),
            getElement<HTMLButtonElement>('historyCloseBtn'),
            getElement<HTMLElement>('historyList'),
            this.api,
            (event) => this.handleSessionLoaded(event)
        );

        // Initialize visit history panel
        this.visitHistoryPanel = new VisitHistoryPanel(
            getElement<HTMLElement>('visitHistoryPanel'),
            getElement<HTMLElement>('visitHistoryOverlay'),
            getElement<HTMLButtonElement>('visitHistoryCloseBtn'),
            getElement<HTMLElement>('visitHistoryList'),
            getElement<HTMLSelectElement>('visitSortBy'),
            this.api,
            () => this.restaurants,
            (restaurantId) => this.loadRestaurants(restaurantId),
            async () => { await this.loadRestaurants(); }
        );

        // Initialize filter config panel
        this.filterConfigPanel = new FilterConfigPanel(
            getElement<HTMLElement>('configOverlay'),
            getElement<HTMLElement>('configPanel'),
            getElement<HTMLButtonElement>('configCloseBtn'),
            getElement<HTMLButtonElement>('configUseCurrentBtn'),
            getElement<HTMLElement>('configLocationStatus'),
            getElement<HTMLInputElement>('configStartingAddress'),
            getElement<HTMLSelectElement>('configTravelMode'),
            getElement<HTMLInputElement>('configMaxMinutes'),
            getElement<HTMLElement>('configMaxMinutesLabel'),
            getElement<HTMLButtonElement>('configApplyBtn'),
            getElement<HTMLElement>('configRestaurantList'),
            getElement<HTMLElement>('configHint'),
            getElement<HTMLElement>('configAddressAutocomplete'),
            this.api,
            this.location,
            () => this.sessionId,
            () => { /* onFilterApplied - no action needed */ },
            () => this.updateFilterSummary()
        );

        // Initialize restaurant form manager
        this.restaurantForm = new RestaurantFormManager(
            getElement<HTMLFormElement>('restaurantForm'),
            getElement<HTMLElement>('restaurantFormContainer'),
            getElement<HTMLButtonElement>('cancelFormBtn'),
            getElement<HTMLElement>('fastFillAutocomplete'),
            getElement<HTMLElement>('fastFillRecognized'),
            this.api,
            async () => { await this.loadRestaurants(); },
            () => { /* onFormHide - no action needed */ }
        );

        this.init();
    }

    private async init(): Promise<void> {
        // Chat events
        this.chatForm.addEventListener('submit', (e) => this.handleSubmit(e));
        this.clearBtn.addEventListener('click', () => this.clearChat());
        this.cancelBtn.addEventListener('click', () => this.handleCancel());
        this.messageInput.addEventListener('input', () => this.autoResize());
        this.messageInput.addEventListener('keydown', (e) => this.handleKeydown(e));
        this.modelSelect.addEventListener('change', () => this.handleModelChange());

        // Filter summary bar events
        this.filterSummaryBar.addEventListener('click', () => this.filterConfigPanel.open());
        this.recommendActionBtn.addEventListener('click', () => this.handleQuickRecommend());

        // History panel events
        const historyBtn = getElement<HTMLButtonElement>('historyBtn');
        historyBtn.addEventListener('click', () => this.sessionHistoryPanel.open());

        // Visit history panel events
        const visitHistoryBtn = getElement<HTMLButtonElement>('visitHistoryBtn');
        visitHistoryBtn.addEventListener('click', () => this.visitHistoryPanel.open());

        // Mobile menu events
        this.mobileMenuBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            this.mobileMenu.classList.toggle('open');
        });
        document.addEventListener('click', (e) => {
            if (!this.mobileMenu.contains(e.target as Node) && !this.mobileMenuBtn.contains(e.target as Node)) {
                this.mobileMenu.classList.remove('open');
            }
        });

        // Language toggle events
        this.langButtons.forEach(btn => {
            btn.addEventListener('click', () => this.handleLanguageChange(btn.dataset.lang as Language));
        });

        // Navigation events
        this.navTabs.forEach(tab => {
            tab.addEventListener('click', () => this.handleNavChange(tab.dataset.view as ViewType));
        });

        // Restaurant events
        this.addRestaurantBtn.addEventListener('click', () => this.restaurantForm.show());
        this.magicImportBtn.addEventListener('click', () => {
            this.restaurantForm.showMagicImportModal(() => this.loadRestaurants());
        });

        // User events
        this.userSelect.addEventListener('change', () => this.handleUserChange());
        this.addUserBtn.style.display = 'none';

        // Restaurant search events
        this.restaurantSearchInput.addEventListener('input', () => this.scheduleRestaurantSearch());

        // Restaurant sorting events
        this.restaurantSortBy.addEventListener('change', () => this.handleSortChange());
        this.restaurantSortOrderBtn.addEventListener('click', () => this.toggleSortOrder());
        this.loadSortPreferences();

        // Load saved language preference OR set default based on URL
        const savedLanguage = localStorage.getItem(STORAGE_KEYS.LANGUAGE) as Language | null;
        if (savedLanguage && (savedLanguage === 'en' || savedLanguage === 'zh')) {
            this.handleLanguageChange(savedLanguage);
        } else {
            const defaultLang = this.getDefaultLanguageFromUrl();
            this.handleLanguageChange(defaultLang);
            localStorage.setItem(STORAGE_KEYS.LANGUAGE, defaultLang);
        }

        // Validate auth token before loading users
        this.authToken = await this.authModal.validate();

        // Load saved user preference
        const savedUser = localStorage.getItem(STORAGE_KEYS.USER);
        if (savedUser) {
            this.selectedUserId = savedUser;
        }

        await this.loadUsers();
        await this.loadModels();

        // Initialize filter summary bar
        this.initializeFilterLocation();
    }

    private async initializeFilterLocation(): Promise<void> {
        this.updateFilterSummary();
        await this.refreshLocation();
        this.startLocationRefreshInterval();

        document.addEventListener('visibilitychange', () => {
            if (document.hidden) {
                this.stopLocationRefreshInterval();
            } else {
                this.refreshLocation();
                this.startLocationRefreshInterval();
            }
        });
    }

    private startLocationRefreshInterval(): void {
        this.stopLocationRefreshInterval();
        this.locationRefreshIntervalId = window.setInterval(() => {
            this.refreshLocation();
        }, LOCATION_REFRESH_INTERVAL_MS);
    }

    private stopLocationRefreshInterval(): void {
        if (this.locationRefreshIntervalId !== null) {
            window.clearInterval(this.locationRefreshIntervalId);
            this.locationRefreshIntervalId = null;
        }
    }

    private async refreshLocation(): Promise<void> {
        try {
            await this.getLocationWithAddress();
        } catch {
            // Keep existing cached location
        }
    }

    private async getLocationWithAddress(): Promise<void> {
        if (localStorage.getItem(STORAGE_KEYS.RECOMMEND_MANUAL_LOCATION) === 'true') {
            return;
        }

        return new Promise((resolve, reject) => {
            if (!navigator.geolocation) {
                reject(new Error('Geolocation not supported'));
                return;
            }

            navigator.geolocation.getCurrentPosition(
                async (position) => {
                    const lat = position.coords.latitude;
                    const lng = position.coords.longitude;

                    localStorage.setItem(STORAGE_KEYS.RECOMMEND_LAT, String(lat));
                    localStorage.setItem(STORAGE_KEYS.RECOMMEND_LNG, String(lng));

                    // Check if location has moved enough to warrant a new geocode call
                    const lastGeocodedLat = Number(localStorage.getItem(STORAGE_KEYS.LAST_GEOCODED_LAT));
                    const lastGeocodedLng = Number(localStorage.getItem(STORAGE_KEYS.LAST_GEOCODED_LNG));

                    const hasLastGeocoded = Number.isFinite(lastGeocodedLat) && Number.isFinite(lastGeocodedLng);
                    const distance = hasLastGeocoded
                        ? haversineDistance(lastGeocodedLat, lastGeocodedLng, lat, lng)
                        : Infinity;

                    // Only call reverse geocode if moved more than threshold or no previous geocode
                    if (distance > MIN_GEOCODE_DISTANCE_METERS) {
                        try {
                            const geoResult = await this.location.reverseGeocode(lat, lng, this.api);
                            localStorage.setItem(STORAGE_KEYS.RECOMMEND_ADDRESS, geoResult.address);
                            const normalizedCountry = normalizeCountryCode(geoResult.country_code);
                            if (normalizedCountry) {
                                localStorage.setItem(STORAGE_KEYS.RECOMMEND_COUNTRY, normalizedCountry);
                            }
                            // Store the coordinates we just geocoded
                            localStorage.setItem(STORAGE_KEYS.LAST_GEOCODED_LAT, String(lat));
                            localStorage.setItem(STORAGE_KEYS.LAST_GEOCODED_LNG, String(lng));
                        } catch {
                            // Keep previous address
                        }
                    }

                    this.updateFilterSummary();
                    resolve();
                },
                (error) => {
                    reject(error);
                },
                { enableHighAccuracy: true, timeout: 10000, maximumAge: 30000 }
            );
        });
    }

    private getDefaultLanguageFromUrl(): Language {
        const path = window.location.pathname.toLowerCase();
        if (path.includes('/jt7sm')) {
            return 'zh';
        }
        return 'en';
    }

    private updateFilterSummary(): void {
        const mode = (localStorage.getItem(STORAGE_KEYS.RECOMMEND_TRAVEL_MODE) as TravelMode) || 'DRIVE';
        const maxMinutes = Number(localStorage.getItem(STORAGE_KEYS.RECOMMEND_MAX_MINUTES)) || 20;

        const iconMap: Record<TravelMode, string> = {
            'DRIVE': `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M16 6L19 10H5L8 6H16Z"/><rect x="3" y="10" width="18" height="8" rx="2"/><circle cx="7" cy="18" r="2"/><circle cx="17" cy="18" r="2"/></svg>`,
            'TRANSIT': `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="4" y="3" width="16" height="14" rx="2"/><line x1="4" y1="10" x2="20" y2="10"/><circle cx="8" cy="17" r="2"/><circle cx="16" cy="17" r="2"/><line x1="8" y1="19" x2="8" y2="21"/><line x1="16" y1="19" x2="16" y2="21"/></svg>`,
            'WALK': `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="4" r="2"/><path d="M14 10l-2 5-2-5"/><path d="M12 10v5"/><path d="M10 22l2-7 2 7"/></svg>`,
            'BICYCLE': `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="5" cy="17" r="3"/><circle cx="19" cy="17" r="3"/><path d="M12 17l-3-7h6l3 7"/><path d="M9 10l3-4"/></svg>`
        };
        this.filterSummaryIcon.innerHTML = iconMap[mode];

        let locationText = i18n.filterSummaryNoLocation;
        const savedLat = localStorage.getItem(STORAGE_KEYS.RECOMMEND_LAT);
        const savedLng = localStorage.getItem(STORAGE_KEYS.RECOMMEND_LNG);
        const savedAddress = localStorage.getItem(STORAGE_KEYS.RECOMMEND_ADDRESS);
        const isManualLocation = localStorage.getItem(STORAGE_KEYS.RECOMMEND_MANUAL_LOCATION) === 'true';

        let locationIndicator = '';

        if (savedLat && savedLng) {
            locationIndicator = isManualLocation ? '📍 ' : '';
            if (savedAddress) {
                const maxLen = 25;
                locationText = savedAddress.length > maxLen
                    ? savedAddress.substring(0, maxLen) + '...'
                    : savedAddress;
            } else {
                locationText = i18n.currentLocation;
            }
        }

        this.filterSummaryText.textContent = `${maxMinutes} ${i18n.filterSummaryMinFrom} ${locationIndicator}${locationText}`;
    }

    private async handleQuickRecommend(): Promise<void> {
        this.setRecommendButtonLoading(true);

        try {
            let savedLat = localStorage.getItem(STORAGE_KEYS.RECOMMEND_LAT);
            let savedLng = localStorage.getItem(STORAGE_KEYS.RECOMMEND_LNG);

            if (!savedLat || !savedLng) {
                try {
                    await this.getLocationWithAddress();
                    savedLat = localStorage.getItem(STORAGE_KEYS.RECOMMEND_LAT);
                    savedLng = localStorage.getItem(STORAGE_KEYS.RECOMMEND_LNG);
                } catch {
                    this.filterConfigPanel.open(i18n.configLocationRequiredHint);
                    return;
                }
            }

            // Apply filter and send recommendation
            await this.filterConfigPanel.apply();
            const filteredRestaurants = this.filterConfigPanel.getFilteredRestaurants();

            if (filteredRestaurants.length > 0) {
                this.sendRecommendationRequest();
            } else {
                this.filterConfigPanel.open();
            }
        } finally {
            this.setRecommendButtonLoading(false);
        }
    }

    private setRecommendButtonLoading(loading: boolean): void {
        const textSpan = this.recommendActionBtn.querySelector('.recommend-action-text');
        if (loading) {
            this.recommendActionBtn.disabled = true;
            this.recommendActionBtn.classList.add('loading');
            if (textSpan) textSpan.textContent = i18n.recommendLoading;
        } else {
            this.recommendActionBtn.disabled = false;
            this.recommendActionBtn.classList.remove('loading');
            if (textSpan) textSpan.textContent = i18n.recommendBtnText;
        }
    }

    private sendRecommendationRequest(): void {
        const filteredRestaurants = this.filterConfigPanel.getFilteredRestaurants();
        if (filteredRestaurants.length === 0) return;

        // Switch to chat view
        const chatTab = document.querySelector('[data-view="chat"]') as HTMLButtonElement;
        if (chatTab) chatTab.click();

        const nowIso = getClientTimeContext().local_time_iso;
        const settings = this.filterConfigPanel.getSettings();

        const message = getLanguage() === 'zh'
            ? `请帮我推荐今天去哪里吃饭。当前时间：${nowIso}。我已经配置了筛选条件（出发地到餐厅最多 ${settings.maxMinutes} 分钟，交通方式：${settings.mode}）。请从提供的餐厅列表中推荐 1-3 家，并说明理由（包含预计路程时间）。`
            : `Please recommend where I should eat today. Current time: ${nowIso}. I've configured the filter (max ${settings.maxMinutes} min travel time by ${settings.mode}). Please recommend 1-3 restaurants from the provided list and explain why (include estimated travel time).`;

        this.messageInput.value = message;
        this.autoResize();
        this.chatForm.dispatchEvent(new Event('submit'));
    }

    private getFilterSettings(): FilterSettings | null {
        const savedLat = localStorage.getItem(STORAGE_KEYS.RECOMMEND_LAT);
        const savedLng = localStorage.getItem(STORAGE_KEYS.RECOMMEND_LNG);
        const savedMode = localStorage.getItem(STORAGE_KEYS.RECOMMEND_TRAVEL_MODE);
        const savedMax = localStorage.getItem(STORAGE_KEYS.RECOMMEND_MAX_MINUTES);

        if (savedLat && savedLng) {
            const lat = Number(savedLat);
            const lng = Number(savedLng);
            if (Number.isFinite(lat) && Number.isFinite(lng)) {
                return {
                    origin_lat: lat,
                    origin_lng: lng,
                    max_travel_time_minutes: savedMax ? Number(savedMax) : 20,
                    travel_mode: savedMode || 'DRIVE'
                };
            }
        }
        return null;
    }

    // Navigation methods
    private handleNavChange(view: ViewType): void {
        this.navTabs.forEach(tab => {
            tab.classList.toggle('active', tab.dataset.view === view);
        });

        this.chatView.classList.toggle('hidden', view !== 'chat');
        this.restaurantsView.classList.toggle('hidden', view !== 'restaurants');

        if (view === 'restaurants') {
            this.loadRestaurants();
        }
    }

    // User methods
    private async loadUsers(): Promise<void> {
        try {
            const response = await fetch('api/users', {
                headers: { 'X-Auth-Token': this.authToken }
            });
            if (response.status === 401) {
                localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
                this.authToken = await this.authModal.validate();
                return this.loadUsers();
            }
            if (!response.ok) {
                throw new Error('Failed to load users');
            }

            const data: UsersListResponse = await response.json();

            this.userSelect.innerHTML = '';
            for (const user of data.users) {
                const option = document.createElement('option');
                option.value = user.id;
                option.textContent = user.name;
                this.userSelect.appendChild(option);
            }

            const validUserIds = data.users.map(u => u.id);
            if (validUserIds.includes(this.selectedUserId)) {
                this.userSelect.value = this.selectedUserId;
            } else {
                this.selectedUserId = 'DEBUG';
                this.userSelect.value = 'DEBUG';
            }
        } catch (error) {
            console.error('Error loading users:', error);
            const option = document.createElement('option');
            option.value = 'DEBUG';
            option.textContent = 'DEBUG';
            this.userSelect.appendChild(option);
            this.selectedUserId = 'DEBUG';
        }
    }

    private handleUserChange(): void {
        this.selectedUserId = this.userSelect.value;
        localStorage.setItem(STORAGE_KEYS.USER, this.selectedUserId);

        this.travelTimes.clear();
        this.restaurants = [];
        this.clearChat();

        if (!this.restaurantsView.classList.contains('hidden')) {
            this.loadRestaurants();
        }
    }

    private getUserHeaders(): HeadersInit {
        return {
            'X-User-Id': this.selectedUserId,
            'X-Auth-Token': this.authToken
        };
    }

    // Language methods
    private handleLanguageChange(lang: Language): void {
        setLanguage(lang);
        localStorage.setItem(STORAGE_KEYS.LANGUAGE, lang);

        this.langButtons.forEach(btn => {
            btn.classList.toggle('active', btn.dataset.lang === lang);
        });

        this.updateUIStrings();
    }

    private updateUIStrings(): void {
        applyTranslations();
        this.filterConfigPanel.updateLanguage();

        this.updateFilterSummary();
    }

    // Model methods
    private async loadModels(): Promise<void> {
        try {
            const data = await this.api.fetch<ModelsResponse>('api/models');

            this.modelSelect.innerHTML = '';
            for (const model of data.models) {
                const option = document.createElement('option');
                option.value = model.id;
                option.textContent = model.name;
                this.modelSelect.appendChild(option);
            }

            const savedModel = localStorage.getItem(STORAGE_KEYS.MODEL);
            if (savedModel && data.models.some(m => m.id === savedModel)) {
                this.selectedModel = savedModel;
            } else {
                this.selectedModel = data.default;
            }
            this.modelSelect.value = this.selectedModel;
        } catch (error) {
            console.error('Error loading models:', error);
        }
    }

    private handleModelChange(): void {
        this.selectedModel = this.modelSelect.value;
        localStorage.setItem(STORAGE_KEYS.MODEL, this.selectedModel);
    }

    // Chat methods
    private generateSessionId(): string {
        return 'sess_' + Date.now() + '_' + Math.random().toString(36).substring(2, 11);
    }

    private autoResize(): void {
        this.messageInput.style.height = 'auto';
        this.messageInput.style.height = `${Math.min(this.messageInput.scrollHeight, 200)}px`;
    }

    private handleKeydown(e: KeyboardEvent): void {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            this.chatForm.dispatchEvent(new Event('submit'));
        }
    }

    private async handleSubmit(e: Event): Promise<void> {
        e.preventDefault();

        if (this.isSending) return;

        const content = this.messageInput.value.trim();
        const image = this.imageUpload.getImage();
        const imageDataUrl = this.imageUpload.getPreviewDataUrl();
        const audio = this.audioRecorder.getAudio();

        if (!content && !image && !audio) return;

        if (this.isViewingHistory) {
            this.isViewingHistory = false;
            this.chatViewComponent.removeHistoryNotice();
        }

        this.chatViewComponent.removeWelcomeMessage();

        // Add user message
        const userMessageElement = this.chatViewComponent.addMessage({
            content,
            type: 'user',
            imageDataUrl: imageDataUrl || undefined,
            isCollapsible: false,
            audioDurationSeconds: audio?.duration_seconds,
            timestamp: new Date(),
        });
        this.chatViewComponent.setCurrentUserMessage(userMessageElement);

        // Clear inputs
        this.messageInput.value = '';
        this.autoResize();
        this.imageUpload.clear();
        this.audioRecorder.clear();

        // Add assistant placeholder
        const assistantMessageElement = this.chatViewComponent.addMessage({ content: '', type: 'assistant' });
        const contentDiv = assistantMessageElement.querySelector('.message-content') as HTMLElement;
        contentDiv.innerHTML = `<span class="thinking-indicator"><span class="thinking-emoji">🤔</span><span>${i18n.thinking}<span class="thinking-dots"></span></span></span>`;

        this.isSending = true;
        this.sendBtn.disabled = true;
        this.showCancelButton(true);
        this.cancelBtn.disabled = false;

        try {
            const filterSettings = this.getFilterSettings();
            await this.chatViewComponent.sendMessageStream(
                content,
                assistantMessageElement,
                this.sessionId,
                this.selectedModel,
                this.selectedUserId,
                image || undefined,
                audio || undefined,
                filterSettings
            );
        } catch (error) {
            const isAborted = error instanceof Error && error.message === 'chat_stream_aborted';
            if (isAborted) {
                contentDiv.innerHTML = `<span class="response-stopped">${i18n.responseStopped}</span>`;
            } else {
                console.error('Error sending message:', error);
                const errorText = error instanceof Error ? error.message : i18n.networkError;
                contentDiv.innerHTML = '';
                this.chatViewComponent.addErrorMessage(errorText);
            }
        } finally {
            this.isSending = false;
            this.sendBtn.disabled = false;
            this.showCancelButton(false);
            this.chatViewComponent.setCurrentUserMessage(null);
        }
    }

    private clearChat(): void {
        this.sessionId = this.generateSessionId();
        this.filterConfigPanel.updateSessionId(this.sessionId);
        this.isViewingHistory = false;
        this.chatViewComponent.clear();
        this.chatContainer.innerHTML = this.chatViewComponent.renderWelcomeMessage(this.restaurants.length);
    }

    private handleCancel(): void {
        if (!this.isSending) return;
        this.cancelBtn.disabled = true;
        this.chatViewComponent.cancelActiveStream();
        this.isSending = false;
        this.sendBtn.disabled = false;
        this.showCancelButton(false);
    }

    private showCancelButton(visible: boolean): void {
        this.cancelBtn.classList.toggle('hidden', !visible);
    }

    private handleSessionLoaded(event: SessionLoadedEvent): void {
        this.chatViewComponent.clear();
        this.sessionId = event.sessionId;
        this.filterConfigPanel.updateSessionId(this.sessionId);
        this.isViewingHistory = true;

        this.chatViewComponent.addHistoryNotice(event.createdAt);

        // Render messages
        for (const msg of event.messages) {
            const msgEl = this.chatViewComponent.addMessage({
                content: msg.content,
                type: msg.role as 'user' | 'assistant',
                toolResults: msg.tool_results || undefined,
            });

            if (msg.thoughts) {
                this.chatViewComponent.addThoughtsToMessage(msgEl, msg.thoughts);
            }
        }
    }

    // Restaurant list methods
    private async loadRestaurants(keepOpenRestaurantId?: string): Promise<void> {
        const openDetails = new Set<string>();
        this.restaurantList.querySelectorAll('.visit-history[open]').forEach(details => {
            const card = details.closest('.restaurant-card') as HTMLElement;
            if (card?.dataset.restaurantId) {
                openDetails.add(card.dataset.restaurantId);
            }
        });
        if (keepOpenRestaurantId) {
            openDetails.add(keepOpenRestaurantId);
        }

        try {
            const headers = this.getUserHeaders();
            const [restaurantsRes, cuisinesRes, tagsRes] = await Promise.all([
                fetch('api/restaurants', { headers }),
                fetch('api/cuisines', { headers }),
                fetch('api/tags', { headers })
            ]);

            if (restaurantsRes.status === 401) {
                localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
                this.authToken = await this.authModal.validate();
                return this.loadRestaurants(keepOpenRestaurantId);
            }
            if (!restaurantsRes.ok) {
                throw new Error('Failed to load restaurants');
            }

            const data: RestaurantsListResponse = await restaurantsRes.json();
            this.restaurants = data.restaurants;

            this.updateOnboardingVisibility();

            if (cuisinesRes.ok) {
                const cuisinesData: CuisinesResponse = await cuisinesRes.json();
                this.populateCuisineList(cuisinesData.cuisines);
            }

            // Populate available tags for the restaurant form
            if (tagsRes.ok) {
                const tagsData: TagsResponse = await tagsRes.json();
                this.restaurantForm.setAvailableTags(tagsData.tags);
            }

            if (data.restaurants.length === 0) {
                this.restaurantList.innerHTML = `<p class="restaurants-empty" id="restaurantsEmpty">${i18n.restaurantsEmpty}</p>`;
                return;
            }

            const sorted = this.sortRestaurants(data.restaurants);
            this.restaurantList.innerHTML = '';
            for (const restaurant of sorted) {
                const card = this.createRestaurantCard(restaurant);
                this.restaurantList.appendChild(card);

                if (openDetails.has(restaurant.id)) {
                    const details = card.querySelector('.visit-history') as HTMLDetailsElement;
                    if (details) {
                        details.open = true;
                    }
                }
            }
        } catch (error) {
            console.error('Error loading restaurants:', error);
            this.restaurants = [];
            this.restaurantList.innerHTML = `<p class="restaurants-empty">${i18n.restaurantsEmpty}</p>`;
        }
    }

    private updateOnboardingVisibility(): void {
        this.chatViewComponent.updateOnboardingVisibility(this.restaurants.length);
    }

    private populateCuisineList(cuisines: string[]): void {
        const datalist = document.getElementById('cuisineList');
        if (!datalist) return;

        datalist.innerHTML = '';
        for (const cuisine of cuisines) {
            const option = document.createElement('option');
            option.value = cuisine;
            datalist.appendChild(option);
        }
    }

    private formatVisitDate(timestamp: string): string {
        try {
            const date = new Date(timestamp);
            return date.toLocaleDateString(getLanguage() === 'zh' ? 'zh-CN' : 'en-US', {
                month: 'short',
                day: 'numeric'
            });
        } catch {
            return timestamp.substring(0, 10);
        }
    }

    private getLastVisitTime(restaurant: Restaurant): string {
        if (!restaurant.visit_records || restaurant.visit_records.length === 0) {
            return '';
        }
        return restaurant.visit_records.reduce((latest, record) => {
            return record.timestamp > latest ? record.timestamp : latest;
        }, '');
    }

    private sortRestaurants(restaurants: Restaurant[]): Restaurant[] {
        const sorted = [...restaurants].sort((a, b) => {
            let comparison = 0;

            switch (this.currentSortField) {
                case 'name': {
                    const nameA = (a.name_en || '').toLowerCase();
                    const nameB = (b.name_en || '').toLowerCase();
                    comparison = nameA.localeCompare(nameB);
                    break;
                }
                case 'added': {
                    const addedA = a.created_at || '';
                    const addedB = b.created_at || '';
                    comparison = addedB.localeCompare(addedA);
                    break;
                }
                case 'last_visit': {
                    const visitA = this.getLastVisitTime(a);
                    const visitB = this.getLastVisitTime(b);
                    if (visitA && visitB) {
                        comparison = visitB.localeCompare(visitA);
                    } else if (visitA) {
                        comparison = -1;
                    } else if (visitB) {
                        comparison = 1;
                    } else {
                        const addedA = a.created_at || '';
                        const addedB = b.created_at || '';
                        comparison = addedB.localeCompare(addedA);
                    }
                    break;
                }
            }

            if (this.currentSortField === 'name') {
                return this.currentSortOrder === 'asc' ? comparison : -comparison;
            } else {
                return this.currentSortOrder === 'desc' ? comparison : -comparison;
            }
        });

        return sorted;
    }

    private createRestaurantCard(restaurant: Restaurant): HTMLElement {
        const card = document.createElement('div');
        card.className = 'restaurant-card';
        card.dataset.restaurantId = restaurant.id;

        const isZh = getLanguage() === 'zh';
        const name = isZh ? restaurant.name_zh : restaurant.name_en;
        const altName = isZh ? restaurant.name_en : restaurant.name_zh;
        const showAltName = altName && altName !== name;

        const travelTime = this.travelTimes.get(restaurant.id);
        let travelTimeBadge = '';
        if (travelTime) {
            if (travelTime.status === 'OK') {
                travelTimeBadge = `<span class="travel-time-badge">${travelTime.duration_text}</span>`;
            } else if (travelTime.status === 'NO_COORDINATES') {
                travelTimeBadge = `<span class="travel-time-badge no-coords">${i18n.noCoordinates}</span>`;
            } else {
                travelTimeBadge = `<span class="travel-time-badge error">--</span>`;
            }
        }

        // Want to go toggle button
        const wantToGoActive = restaurant.want_to_go ? 'active' : '';
        const wantToGoLabel = restaurant.want_to_go ? i18n.wantToGo : '';
        const wantToGoBtn = `<button class="want-to-go-btn ${wantToGoActive}" data-id="${restaurant.id}" title="${i18n.wantToGo}">🎯${wantToGoLabel ? ` ${wantToGoLabel}` : ''}</button>`;

        let visitHistoryHtml = '';
        if (restaurant.visit_records && restaurant.visit_records.length > 0) {
            const recordsHtml = restaurant.visit_records.map((record, index) => {
                const dateStr = this.formatVisitDate(record.timestamp);
                return `<div class="visit-record" data-index="${index}">
                    <div class="visit-record-header">
                        <span class="visit-date">${dateStr}</span>
                        <div class="visit-record-actions">
                            <button class="visit-edit-btn" data-restaurant-id="${restaurant.id}" data-index="${index}" title="${i18n.editVisit}">✏️</button>
                            <button class="visit-delete-btn" data-restaurant-id="${restaurant.id}" data-index="${index}" title="${i18n.deleteVisit}">🗑️</button>
                        </div>
                    </div>
                    <span class="visit-note">${record.note}</span>
                </div>`;
            }).join('');

            visitHistoryHtml = `
                <details class="visit-history">
                    <summary>${i18n.visitHistoryLabel} (${restaurant.visit_records.length})</summary>
                    <div class="visit-records">${recordsHtml}</div>
                </details>
            `;
        }

        card.innerHTML = `
            <div class="restaurant-card-header">
                <div class="restaurant-card-title">
                    ${wantToGoBtn}
                    <span class="restaurant-name">${name}</span>
                    ${showAltName ? `<span class="restaurant-name-alt">(${altName})</span>` : ''}
                    ${travelTimeBadge}
                </div>
                <span class="restaurant-cuisine">${restaurant.cuisine}</span>
            </div>
            <div class="restaurant-address">${restaurant.address}</div>
            ${restaurant.summary ? `<div class="restaurant-summary">${restaurant.summary}</div>` : ''}
            <div class="dish-photos-container hidden" data-restaurant-id="${restaurant.id}">
                <div class="dish-photos-inline"></div>
            </div>
            ${visitHistoryHtml}
            <div class="restaurant-card-actions">
                <button class="add-visit-btn" data-id="${restaurant.id}">${i18n.addVisit}</button>
                <button class="preview-dishes-btn" data-id="${restaurant.id}">${i18n.previewDishes}</button>
                <button class="edit-btn" data-id="${restaurant.id}">${i18n.edit}</button>
                <button class="delete-btn" data-id="${restaurant.id}">${i18n.delete}</button>
            </div>
        `;

        // Want to go toggle event
        const wantToGoBtnEl = card.querySelector('.want-to-go-btn') as HTMLButtonElement;
        wantToGoBtnEl.addEventListener('click', () => this.toggleWantToGo(restaurant.id, wantToGoBtnEl));

        const addVisitBtn = card.querySelector('.add-visit-btn') as HTMLButtonElement;
        addVisitBtn.addEventListener('click', () => this.showAddVisitModal(restaurant));

        const previewBtn = card.querySelector('.preview-dishes-btn') as HTMLButtonElement;
        previewBtn.addEventListener('click', () => this.toggleDishPhotos(restaurant.id, card));

        const editBtn = card.querySelector('.edit-btn') as HTMLButtonElement;
        editBtn.addEventListener('click', () => this.editRestaurant(restaurant));

        const deleteBtn = card.querySelector('.delete-btn') as HTMLButtonElement;
        deleteBtn.addEventListener('click', () => this.deleteRestaurant(restaurant.id));

        card.querySelectorAll('.visit-delete-btn').forEach(btn => {
            btn.addEventListener('click', (e) => {
                e.stopPropagation();
                const target = e.currentTarget as HTMLButtonElement;
                const restaurantId = target.dataset.restaurantId!;
                const index = parseInt(target.dataset.index!, 10);
                this.deleteVisitRecord(restaurantId, index);
            });
        });

        card.querySelectorAll('.visit-edit-btn').forEach(btn => {
            btn.addEventListener('click', (e) => {
                e.stopPropagation();
                const target = e.currentTarget as HTMLButtonElement;
                const restaurantId = target.dataset.restaurantId!;
                const index = parseInt(target.dataset.index!, 10);
                const record = restaurant.visit_records?.[index];
                if (record) {
                    this.editVisitRecord(restaurantId, index, record.note, record.timestamp);
                }
            });
        });

        return card;
    }

    private async toggleWantToGo(restaurantId: string, btn: HTMLButtonElement): Promise<void> {
        const isCurrentlyActive = btn.classList.contains('active');
        const newWantToGo = !isCurrentlyActive;

        try {
            await this.api.fetch<{ success: boolean }>(`api/restaurants/${restaurantId}/want-to-go`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ want_to_go: newWantToGo })
            });

            btn.classList.toggle('active', newWantToGo);
            // Update button text to show/hide label
            btn.textContent = newWantToGo ? `🎯 ${i18n.wantToGo}` : '🎯';

            // Update the restaurant in local state
            const restaurant = this.restaurants.find(r => r.id === restaurantId);
            if (restaurant) {
                restaurant.want_to_go = newWantToGo;
            }
        } catch (error) {
            console.error('Error toggling want-to-go:', error);
        }
    }

    private editRestaurant(restaurant: Restaurant): void {
        this.restaurantForm.show(restaurant);
        const formContainer = document.getElementById('restaurantFormContainer');
        formContainer?.scrollIntoView({ behavior: 'smooth' });
    }

    private async deleteRestaurant(id: string): Promise<void> {
        if (!confirm(i18n.deleteConfirm)) return;

        try {
            await this.api.fetch<void>(`api/restaurants/${id}`, { method: 'DELETE' });
            await this.loadRestaurants();
        } catch (error) {
            console.error('Error deleting restaurant:', error);
        }
    }

    private async toggleDishPhotos(restaurantId: string, card: HTMLElement): Promise<void> {
        const container = card.querySelector('.dish-photos-container') as HTMLElement;
        const photosInline = container.querySelector('.dish-photos-inline') as HTMLElement;
        const previewBtn = card.querySelector('.preview-dishes-btn') as HTMLButtonElement;

        if (!container.classList.contains('hidden')) {
            container.classList.add('hidden');
            previewBtn.textContent = i18n.previewDishes;
            return;
        }

        if (photosInline.children.length > 0) {
            container.classList.remove('hidden');
            previewBtn.textContent = i18n.hideDishes;
            return;
        }

        photosInline.innerHTML = `<p class="dish-photos-loading">${i18n.loadingPhotos}</p>`;
        container.classList.remove('hidden');
        previewBtn.textContent = i18n.hideDishes;

        try {
            const data = await this.api.fetch<RestaurantPhotosResponse>(`api/restaurants/${restaurantId}/photos`);

            if (data.photos.length === 0) {
                photosInline.innerHTML = `<p class="dish-photos-empty">${i18n.noDishPhotos}</p>`;
            } else {
                const imagesHtml = data.photos.map((url, index) =>
                    `<div class="dish-photo-wrapper">
                        <img src="${url}" alt="Dish photo" loading="lazy" data-index="${index}">
                        <div class="dish-photo-attribution">Google</div>
                    </div>`
                ).join('');
                photosInline.innerHTML = imagesHtml;

                photosInline.querySelectorAll('img').forEach(img => {
                    img.addEventListener('click', () => {
                        const index = parseInt(img.dataset.index || '0', 10);
                        this.photoLightbox.open(data.photos, index);
                    });
                });
            }
        } catch (error) {
            console.error('Error loading photos:', error);
            photosInline.innerHTML = `<p class="dish-photos-empty">${i18n.noDishPhotos}</p>`;
        }
    }

    // Visit record methods
    private showAddVisitModal(restaurant: Restaurant): void {
        const modal = document.createElement('div');
        modal.className = 'visit-modal-overlay';

        const today = new Date().toISOString().split('T')[0];
        const restaurantName = getLanguage() === 'zh' ? restaurant.name_zh : restaurant.name_en;

        modal.innerHTML = `
            <div class="visit-modal">
                <h3>${i18n.addVisitTitle}</h3>
                <p class="visit-modal-restaurant">${restaurantName}</p>
                <div class="visit-modal-form">
                    <label for="visitDate">${i18n.dateLabel}:</label>
                    <input type="date" id="visitDate" value="${today}">
                    <label for="visitNote">${i18n.noteLabel}:</label>
                    <textarea id="visitNote" rows="3" placeholder="${i18n.addVisitPlaceholder}"></textarea>
                </div>
                <div class="visit-modal-actions">
                    <button class="btn-secondary" id="visitCancel">${i18n.cancel}</button>
                    <button class="btn-primary" id="visitSave">${i18n.save}</button>
                </div>
            </div>
        `;

        document.body.appendChild(modal);

        const closeModal = () => modal.remove();

        modal.querySelector('#visitCancel')?.addEventListener('click', closeModal);
        modal.addEventListener('click', (e) => {
            if (e.target === modal) closeModal();
        });

        modal.querySelector('#visitSave')?.addEventListener('click', async () => {
            const dateInput = modal.querySelector('#visitDate') as HTMLInputElement;
            const noteInput = modal.querySelector('#visitNote') as HTMLTextAreaElement;

            const dateStr = dateInput.value;
            const note = noteInput.value.trim();

            if (!note) return;

            const timestamp = new Date(dateStr + 'T12:00:00').toISOString();

            try {
                await this.api.fetch<void>(`api/restaurants/${restaurant.id}/visits`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ timestamp, note })
                });
                closeModal();
                await this.loadRestaurants(restaurant.id);
            } catch (error) {
                console.error('Error adding visit record:', error);
            }
        });

        setTimeout(() => {
            (modal.querySelector('#visitNote') as HTMLTextAreaElement)?.focus();
        }, 100);
    }

    private async deleteVisitRecord(restaurantId: string, visitIndex: number): Promise<void> {
        if (!confirm(i18n.visitHistoryDeleteConfirm)) return;

        try {
            await this.api.fetch<void>(`api/restaurants/${restaurantId}/visits/${visitIndex}`, { method: 'DELETE' });
            await this.loadRestaurants(restaurantId);
        } catch (error) {
            console.error('Error deleting visit record:', error);
        }
    }

    private editVisitRecord(restaurantId: string, visitIndex: number, currentNote: string, currentTimestamp: string): void {
        const modal = document.createElement('div');
        modal.className = 'visit-modal-overlay';

        const currentDate = currentTimestamp.split('T')[0];

        modal.innerHTML = `
            <div class="visit-modal">
                <h3>${i18n.editVisitTitle}</h3>
                <div class="visit-modal-form">
                    <label for="visitDate">${i18n.dateLabel}:</label>
                    <input type="date" id="visitDate" value="${currentDate}">
                    <label for="visitNote">${i18n.noteLabel}:</label>
                    <textarea id="visitNote" rows="3">${currentNote}</textarea>
                </div>
                <div class="visit-modal-actions">
                    <button class="btn-secondary" id="visitCancel">${i18n.cancel}</button>
                    <button class="btn-primary" id="visitSave">${i18n.save}</button>
                </div>
            </div>
        `;

        document.body.appendChild(modal);

        const closeModal = () => modal.remove();

        modal.querySelector('#visitCancel')?.addEventListener('click', closeModal);
        modal.addEventListener('click', (e) => {
            if (e.target === modal) closeModal();
        });

        modal.querySelector('#visitSave')?.addEventListener('click', async () => {
            const dateInput = modal.querySelector('#visitDate') as HTMLInputElement;
            const noteInput = modal.querySelector('#visitNote') as HTMLTextAreaElement;

            const dateStr = dateInput.value;
            const note = noteInput.value.trim();

            if (!note) return;

            const timestamp = new Date(dateStr + 'T12:00:00').toISOString();

            try {
                await this.api.fetch<void>(`api/restaurants/${restaurantId}/visits/${visitIndex}`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ timestamp, note })
                });
                closeModal();
                await this.loadRestaurants(restaurantId);
            } catch (error) {
                console.error('Error updating visit record:', error);
            }
        });

        setTimeout(() => {
            (modal.querySelector('#visitNote') as HTMLTextAreaElement)?.focus();
        }, 100);
    }

    // Restaurant search and sort methods
    private scheduleRestaurantSearch(): void {
        if (this.restaurantSearchTimer !== null) {
            window.clearTimeout(this.restaurantSearchTimer);
        }
        this.restaurantSearchTimer = window.setTimeout(() => {
            this.filterRestaurantList();
        }, 150);
    }

    private filterRestaurantList(): void {
        const query = this.restaurantSearchInput.value.trim().toLowerCase();

        if (!query) {
            this.refreshRestaurantCards();
            return;
        }

        const filtered = this.restaurants.filter(r => {
            const searchableText = [
                r.name_en,
                r.name_zh || '',
                r.cuisine,
                r.address,
                ...(r.keywords || [])
            ].join(' ').toLowerCase();
            return searchableText.includes(query);
        });

        this.restaurantList.innerHTML = '';
        if (filtered.length === 0) {
            const emptyMsg = document.createElement('p');
            emptyMsg.className = 'restaurants-empty';
            emptyMsg.textContent = i18n.noMatchingRestaurantsSearch;
            this.restaurantList.appendChild(emptyMsg);
        } else {
            const sortedFiltered = this.sortRestaurants(filtered);
            for (const restaurant of sortedFiltered) {
                const card = this.createRestaurantCard(restaurant);
                this.restaurantList.appendChild(card);
            }
        }
    }

    private refreshRestaurantCards(): void {
        if (this.restaurants.length === 0) {
            this.restaurantList.innerHTML = `<p class="restaurants-empty" id="restaurantsEmpty">${i18n.restaurantsEmpty}</p>`;
            return;
        }

        const sorted = this.sortRestaurants(this.restaurants);
        this.restaurantList.innerHTML = '';
        for (const restaurant of sorted) {
            const card = this.createRestaurantCard(restaurant);
            this.restaurantList.appendChild(card);
        }
    }

    private loadSortPreferences(): void {
        const savedSortBy = localStorage.getItem(STORAGE_KEYS.RESTAURANT_SORT_BY) as RestaurantSortField | null;
        const savedSortOrder = localStorage.getItem(STORAGE_KEYS.RESTAURANT_SORT_ORDER) as SortOrder | null;

        if (savedSortBy && ['name', 'added', 'last_visit'].includes(savedSortBy)) {
            this.currentSortField = savedSortBy;
            this.restaurantSortBy.value = savedSortBy;
        }

        if (savedSortOrder && ['asc', 'desc'].includes(savedSortOrder)) {
            this.currentSortOrder = savedSortOrder;
            this.restaurantSortOrderBtn.dataset.order = savedSortOrder;
        }
    }

    private handleSortChange(): void {
        this.currentSortField = this.restaurantSortBy.value as RestaurantSortField;
        localStorage.setItem(STORAGE_KEYS.RESTAURANT_SORT_BY, this.currentSortField);
        this.refreshRestaurantCards();
    }

    private toggleSortOrder(): void {
        this.currentSortOrder = this.currentSortOrder === 'asc' ? 'desc' : 'asc';
        this.restaurantSortOrderBtn.dataset.order = this.currentSortOrder;
        localStorage.setItem(STORAGE_KEYS.RESTAURANT_SORT_ORDER, this.currentSortOrder);
        this.refreshRestaurantCards();
    }
}

// Note: App initialization happens in `src/main.ts` to avoid double-binding event listeners.
